import React from "react";
import { ShipmentReconciliationSummaryTile } from "../../summary-tile/shipmentReconciliationSummaryTile";
import { AccessorialRevenueTrendIndicators, ShipmentReconciliationSummaryTileTypes, ShipmentReconciliationSummaryTypes } from "../../../../app/data/shipment-reconciliation/models";
import NumberFormat from "react-number-format";

interface RevenueInsightsSummaryProps {
    summaryData: AccessorialRevenueTrendIndicators;
    isLoading: boolean;
    isShowSummaryMetrics: boolean;
    isShowSystemMetrics: boolean;
    isShowOperationsMetrics: boolean;
    isShowBillingMetrics: boolean;
}

export const RevenueInsightsSummary: React.FC<RevenueInsightsSummaryProps> = ({
    summaryData,
    isLoading,
    isShowSummaryMetrics,
    isShowSystemMetrics,
    isShowOperationsMetrics,
    isShowBillingMetrics
}) => {

    return (<div className="xgs-accessorial-revenue-insights__summary">
        {isShowSummaryMetrics &&
            <div className="xgs-accessorial-revenue-insights__summary__row">
                <div className="xgs-accessorial-revenue-insights__summary__row__title">
                    Summary
                </div>
                <div className="xgs-accessorial-revenue-insights__summary__row__tiles">
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.REVIEW_DAYS}
                        isLoading={isLoading}
                        title="Review Days Selected"
                        value={summaryData.reviewDays}
                        legendDescription="Number of weekdays (Mon-Fri) in the selected date range. Count of weekdays"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.REVENUE}
                        isLoading={isLoading}
                        title="Revenue Captured"
                        value={<NumberFormat
                            value={summaryData.acceptedAmount}
                            decimalScale={2}
                            displayType={"text"}
                            thousandSeparator={","}
                            prefix={"$ "}
                        />}
                        legendDescription="Total revenue captured from accessorials approved by Operations and Billing. Formula: Sum(Realized Amount)"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.AVERAGE}
                        isLoading={isLoading}
                        title="Avg. Accessorial Charge"
                        value={<NumberFormat
                            value={summaryData.avaerageRevenuePerAccessorial}
                            decimalScale={2}
                            displayType={"text"}
                            thousandSeparator={","}
                            prefix={"$ "}
                        />}
                        legendDescription="Average charge per approved accessorial. Formula: Total Revenue Captured ÷ Billing-Approved Accessorials"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.APPROVAL_RATE}
                        isLoading={isLoading}
                        title="Avg. Revenue per Day"
                        value={<NumberFormat
                            value={Number((summaryData.acceptedAmount / summaryData.reviewDays).toFixed(2))}
                            decimalScale={2}
                            displayType={"text"}
                            thousandSeparator={","}
                            prefix={"$ "}
                        />}
                        legendDescription="Average daily revenue captured. Formula: Total Revenue Captured ÷ Review Days"
                    />
                </div>
            </div>
        }
        {isShowSystemMetrics &&
            <div className="xgs-accessorial-revenue-insights__summary__row">
                <div className="xgs-accessorial-revenue-insights__summary__row__title">
                    System Metrics
                </div>
                <div className="xgs-accessorial-revenue-insights__summary__row__tiles">
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.TOTAL_SHIPMENTS}
                        isLoading={isLoading}
                        title="Shipments Scanned"
                        value={summaryData.scannedShipments}
                        legendDescription="Total shipments scanned for potential accessorials. Formula: Count of scanned shipments"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.FINDINGS}
                        isLoading={isLoading}
                        title="Findings"
                        value={summaryData.accessorialFindings}
                        legendDescription="Potential accessorials identified during scans. Formula: Count of findings"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.AVERAGE}
                        isLoading={isLoading}
                        title="Avg. Scans per Day"
                        value={summaryData.averageDailyScans}
                        legendDescription="Average daily scans across review days. Formula: Total Scans ÷ Review Days"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.APPROVAL_RATE}
                        isLoading={isLoading}
                        title="Findings Rate"
                        value={`${summaryData.accessorialFindingsRate} %`}
                        legendDescription="Percentage of scanned shipments with findings. Formula: Findings ÷ Scanned Shipments * 100"
                    />
                </div>
            </div>
        }
        {isShowOperationsMetrics &&
            <div className="xgs-accessorial-revenue-insights__summary__row">
                <div className="xgs-accessorial-revenue-insights__summary__row__title">
                    Operations Metrics
                </div>
                <div className="xgs-accessorial-revenue-insights__summary__row__tiles">
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.PENDING_FOR_REVIEW}
                        isLoading={isLoading}
                        title="Pending Review"
                        value={summaryData.operationPending}
                        legendDescription="Accessorials pending Operations review. Formula: Count of pending items"
                        />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.ACCEPTED}
                        isLoading={isLoading}
                        title="Accepted"
                        value={summaryData.operationsAccepted}
                        legendDescription="Accessorials approved by Operations. Formula: Count of accepted items"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.REJECTED}
                        isLoading={isLoading}
                        title="Rejected"
                        value={summaryData.operationsRejected}
                        legendDescription="Accessorials rejected by Operations. Formula: Count of rejected items"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.REVIEW_PROGRESS}
                        isLoading={isLoading}
                        title="Approval Rate"
                        value={`${summaryData.operationsApprovalRate} %`}
                        legendDescription="Approval percentage for Operations-reviewed accessorials. Formula: Accepted ÷ (Accepted + Rejected) * 100"
                    />
                </div>
            </div>
        }
        {isShowBillingMetrics &&
            <div className="xgs-accessorial-revenue-insights__summary__row">
                <div className="xgs-accessorial-revenue-insights__summary__row__title">
                    Billing Metrics
                </div>
                <div className="xgs-accessorial-revenue-insights__summary__row__tiles">
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.PENDING_FOR_REVIEW}
                        isLoading={isLoading}
                        title="Pending Review"
                        value={summaryData.billingPending}
                        legendDescription="Accessorials pending Billing review. Formula: Count of pending items"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.ACCEPTED}
                        isLoading={isLoading}
                        title="Accepted"
                        value={summaryData.billingAccepted}
                        legendDescription="Accessorials approved by Billing. Formula: Count of accepted items"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.REJECTED}
                        isLoading={isLoading}
                        title="Refused"
                        value={summaryData.billingRejected}
                        legendDescription="Accessorials rejected by Billing. Formula: Count of rejected items"
                    />
                    <ShipmentReconciliationSummaryTile
                        tileContentType={ShipmentReconciliationSummaryTileTypes.INFO}
                        type={ShipmentReconciliationSummaryTypes.REVIEW_PROGRESS}
                        isLoading={isLoading}
                        title="Approval Rate"
                        value={`${summaryData.billingApprovalRate} %`}
                        legendDescription="Approval percentage for Billing-reviewed accessorials. Formula: Accepted ÷ (Accepted + Rejected) × 100"
                    />
                </div>
            </div>
        }
    </div>
    )
};